<template>
    <div>
        <PageHeader
            :page="pageHeaderConfig"
            :has-return="true"
            right-side-class="col-xl-4 d-flex justify-content-end align-items-end pb-2"
            @onReturn="() => $router.back()"
        />
        <PlanDiscussion
            v-model="interventionPlan"
            class="mb-3"
        />
        <PlanBoard 
            :main-info="planBoardMainInfo"
            :secondary-info="planBoardSecondaryInfo"
            class="mb-3"
        />

        <CustomCollapse
            id="ip-collapse"
            class="intervention-plan-collapse px-4"
        >
            <CustomCollapseItem
                title="Información de Fortalezas"
                id="collapse-item-strength"
                parent-id="ip-collapse"
                body-background-color="#F7F7F7"
                :checked="!!interventionPlan.strengths.length"
            >
                <PlanStrengths v-model="interventionPlan.strengths" />
            </CustomCollapseItem>
            <CustomCollapseItem
                title="Información de Necesidades"
                id="collapse-item-need"
                parent-id="ip-collapse"
                body-background-color="#F7F7F7"
                :checked="!!interventionPlan.needs.length"
            >
                <PlanNeeds v-model="interventionPlan.needs" />
            </CustomCollapseItem>
            <CustomCollapseItem
                title="Información de Metas y Objetivos"
                id="collapse-item-goals"
                parent-id="ip-collapse"
                body-background-color="#F7F7F7"
                :checked="interventionPlan.goals.some((g) => !!g.items.length)"
            >
                <PlanGoals v-model="interventionPlan.goals" />
            </CustomCollapseItem>
            <CustomCollapseItem
                title="Criterios y Procedimientos"
                id="collapse-item-criterion"
                parent-id="ip-collapse"
                body-background-color="#F7F7F7"
                :checked="!!interventionPlan.criteria"
            >
                <PlanCriterias v-model="interventionPlan.criteria" />
            </CustomCollapseItem>
            <CustomCollapseItem
                title="Información de Especialista"
                id="collapse-item-specialist"
                parent-id="ip-collapse"
                body-background-color="#F7F7F7"
                :checked="!!interventionPlan.writerSpecialistId && !!interventionPlan.serviceSpecialistId"
            >
                <PlanSpecialist v-model="interventionPlan"/>
            </CustomCollapseItem>
            <CustomCollapseItem
                title="Documento"
                id="collapse-item-document"
                parent-id="ip-collapse"
                body-background-color="#F7F7F7"
                :checked="!!interventionPlan.documentId"
            >
                <PlanDocument
                    v-model="interventionPlan"
                    @download="handleDownloadDocument"
                />
            </CustomCollapseItem>
        </CustomCollapse>
    </div>
</template>

<script>
import PageHeader from 'PageHeader';
import alertMixin from 'utils/mixins/alert.mixin';
import { CustomCollapse, CustomCollapseItem } from 'custom-collapse';

import PlanDiscussion from './components/PlanDiscussion.vue';
import PlanBoard from './components/PlanBoard.vue';
import PlanStrengths from './components/PlanStrengths.vue';
import PlanNeeds from './components/PlanNeeds.vue';
import PlanSpecialist from './components/PlanSpecialist.vue';
import PlanCriterias from './components/PlanCriterias.vue';
import PlanDocument from './components/PlanDocument.vue';
import PlanGoals from './components/PlanGoals.vue';

import GeneralService from '@/services/pei/general';
import ReferralService from '@/services/pei/thrpyReferrals';

export default {
    name: 'InterventionPlan',
    mixins: [alertMixin],
    components: {
        PageHeader,
        CustomCollapse,
        CustomCollapseItem,
        PlanDiscussion,
        PlanBoard,
        PlanStrengths,
        PlanNeeds,
        PlanSpecialist,
        PlanCriterias,
        PlanDocument,
        PlanGoals,
    },
    data: () => ({
        pageHeaderConfig: {
            icon: 'fas fa-user-tie',
            title: 'Ver plan de intervención',
            routes: [
                'PEI',
                'Manejo de Referidos',
                'Ver plan de intervención',
            ],
        },
        interventionPlan: {
            date: null,
            endTime: null,
            startTime: null,
            isPlanBilling: null,
            argumentativeSpecialistId: null,
            argumentativeSpecialistName: '',
            writerSpecialistId: null,
            writerSpecialistName: '',
            serviceSpecialistId: null,
            serviceSpecialistName: '',
            admissionSpecialistId: null,
            admissionSpecialistName: '',
            specialistLicenseNumber: null,
            specialistLicenseExpiration: null,
            studentSieId: null,
            studentFullName: null,
            studentAge: '',
            studentImpediment: null,
            studentRegion: null,
            studentMunicipality: null,
            studentPlace: null,
            disciplineName: null,
            modalityTypeName: null,
            frequency: null,
            duration: null,
            documentName: null,
            documentId: null,
            criteria: null,
            strengths: [],
            needs: [],
            goals: [],
        },
    }),
    computed: {
        planBoardMainInfo() {
            return [
                {
                    label: 'SIE:',
                    icon: 'fas fa-address-book',
                    data: this.interventionPlan.studentSieId,
                    cols: 'col-12 col-md-3',
                },
                {
                    label: 'Estudiante:',
                    icon: 'fas fa-address-book',
                    data: this.interventionPlan.studentFullName,
                    cols: 'col-12 col-md-3',
                },
                {
                    label: 'Edad:',
                    icon: 'fas fa-calendar-edit',
                    data: this.interventionPlan.studentAge ?
                        `${this.interventionPlan.studentAge} años` : '',
                    cols: 'col-12 col-md-3',
                },
                {
                    label: 'Impedimento:',
                    icon: 'fas fa-ballot-check',
                    data: this.interventionPlan.studentImpediment,
                    cols: 'col-12 col-md-3',
                },
                {
                    label: 'Región:',
                    icon: 'fas fa-map-marker-alt',
                    data: this.interventionPlan.studentRegion,
                    cols: 'col-12 col-md-3',
                },
                {
                    label: 'Municipio:',
                    icon: 'fas fa-map-marker-alt',
                    data: this.interventionPlan.studentMunicipality,
                    cols: 'col-12 col-md-3',
                },
                {
                    label: 'Código-Escuela:',
                    icon: 'fas fa-graduation-cap',
                    data: this.interventionPlan.studentPlace,
                    cols: 'col-12 col-md-3',
                },
            ];
        },
        planBoardSecondaryInfo() {
            return [
                {
                    label: 'Nombre de Especialista:',
                    data: this.interventionPlan.admissionSpecialistName,
                    cols: 'col-4 col-sm col-12 mt-3',
                },
                {
                    label: 'Tipo de Servicio:',
                    data: this.interventionPlan.disciplineName,
                    cols: 'col-4 col-sm col-12 mt-3',
                },
                {
                    label: 'Modalidad:',
                    data: this.interventionPlan.modalityTypeName,
                    cols: 'col-4 col-sm col-12 mt-3',
                },
                {
                    label: 'Frecuencia:',
                    data: this.interventionPlan.frequency,
                    cols: 'col-4 col-sm col-12 mt-3',
                },
                {
                    label: 'Duración:',
                    data: this.interventionPlan.duration,
                    cols: 'col-4 col-sm col-12 mt-3',
                },
            ];
        },
    },
    methods: {
        async fetchInterventionPlanData(planId, admissionId) {
            const { data } = await ReferralService.getInterventionPlan(
                planId,
                admissionId
            );
            Object.assign(this.interventionPlan, data);
        },
        async handleDownloadDocument(documentId) {
            await GeneralService.getDocument(documentId);
        }
    },
    async mounted() {
        await this.fetchInterventionPlanData(
            this.$route.params.planId,
            this.$route.params.admissionId
        );
    },
};
</script>
  
<style lang="scss" scoped>
@import '@/assets/scss/variables/_colors.scss';

::v-deep .intervention-plan-collapse {
  .card-header {
    background-color: map-get($colors, 'white');
    border-bottom: 1px solid #e1e1e1;
    border-top: 1px solid #e1e1e1;
  }
}
</style>
  